import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import { Clock, Loader2, ArrowRight, ChevronLeft, ChevronRight, Tag, ArrowRightLeft, X } from 'lucide-react';

const STATUS_COLORS = {
  occupied: 'text-green-500',
  leased: 'text-blue-500',
  notice: 'text-orange-500',
  vacant: 'text-red-500',
  model: 'text-orange-400',
  offline: 'text-orange-400',
};

const STATUS_BG = {
  occupied: 'bg-green-500',
  leased: 'bg-blue-500',
  notice: 'bg-orange-500',
  vacant: 'bg-red-500',
  model: 'bg-orange-400',
  offline: 'bg-orange-400',
};

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Shared timeline component used at property, building, and unit levels
// scope: 'property' | 'building' | 'unit'
// ids: { propertyId, buildingId?, unitId? }
// showUnit: whether to show unit number column (true for property/building, false for unit)
// showBuilding: whether to show building column (true for property, false for building/unit)
export default function AitHistoryTimeline({ scope, ids, showUnit = true, showBuilding = false }) {
  const { theme } = useTheme();
  const { fetchHistory } = useAit();
  const isDark = theme === 'dark';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');

  const filters = { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };

  const load = useCallback(async (p, f) => {
    setLoading(true);
    const result = await fetchHistory(scope, ids, p, f || filters);
    setData(result);
    setLoading(false);
  }, [scope, ids, fetchHistory, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load(page, filters);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when ids change
  useEffect(() => {
    setPage(1);
    setDateFrom('');
    setDateTo('');
    setActivePreset('all');
    load(1, {});
  }, [ids.propertyId, ids.buildingId, ids.unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (preset) => {
    setActivePreset(preset);
    setPage(1);
    const today = new Date();
    let from = '';
    let to = '';
    if (preset === '3m') {
      from = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()).toISOString().split('T')[0];
    } else if (preset === '6m') {
      from = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate()).toISOString().split('T')[0];
    } else if (preset === '1y') {
      from = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()).toISOString().split('T')[0];
    } else if (preset === '2y') {
      from = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate()).toISOString().split('T')[0];
    } else if (preset === 'custom') {
      // keep current dateFrom/dateTo, user will set them
      return;
    }
    // 'all' clears both
    setDateFrom(from);
    setDateTo(to);
    load(1, { dateFrom: from || undefined, dateTo: to || undefined });
  };

  const applyCustomRange = () => {
    setPage(1);
    setActivePreset('custom');
    load(1, filters);
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setActivePreset('all');
    setPage(1);
    load(1, {});
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-blue-500" size={20} />
      </div>
    );
  }

  const events = data?.events || [];
  const total = data?.total || 0;
  const perPage = data?.per_page || 50;
  const totalPages = Math.ceil(total / perPage);
  const hasDateFilter = dateFrom || dateTo;

  const presetBtnCls = (key) => `px-2.5 py-1 rounded text-xs transition-colors ${
    activePreset === key
      ? 'bg-blue-600 text-white'
      : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
  }`;

  return (
    <div>
      {/* Date Filter Bar */}
      <div className={`flex flex-wrap items-center gap-2 mb-4 pb-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Period:</span>
        {[
          { key: 'all', label: 'All Time' },
          { key: '3m', label: '3 Months' },
          { key: '6m', label: '6 Months' },
          { key: '1y', label: '1 Year' },
          { key: '2y', label: '2 Years' },
        ].map(p => (
          <button key={p.key} onClick={() => applyPreset(p.key)} className={presetBtnCls(p.key)}>
            {p.label}
          </button>
        ))}
        <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setActivePreset('custom'); }}
          className={`text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
          title="From date"
        />
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>to</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setActivePreset('custom'); }}
          className={`text-xs rounded px-2 py-1 border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
          title="To date"
        />
        {activePreset === 'custom' && (
          <button onClick={applyCustomRange} className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">
            Apply
          </button>
        )}
        {hasDateFilter && (
          <button onClick={clearFilters} className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
            <X size={10} /> Clear
          </button>
        )}
        {total > 0 && (
          <span className={`text-xs ml-auto ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {total} event{total !== 1 ? 's' : ''}{hasDateFilter ? ' in range' : ''}
          </span>
        )}
      </div>
      {/* Empty state */}
      {events.length === 0 && !loading && (
        <div className={`text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Clock size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">{hasDateFilter ? 'No events in this date range.' : 'No history yet.'}</p>
          {!hasDateFilter && <p className="text-xs mt-1">Status changes and designation updates will appear here as they happen.</p>}
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 && <div className="relative">
        {/* Vertical line */}
        <div className={`absolute left-5 top-0 bottom-0 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />

        <div className="space-y-0">
          {events.map((evt, i) => {
            const isStatus = evt.type === 'status_change';
            const isDesignation = evt.type === 'designation_change';

            return (
              <div key={i} className={`relative flex gap-3 pl-2 py-2.5 ${isDark ? 'hover:bg-gray-800/30' : 'hover:bg-gray-50/50'}`}>
                {/* Timeline dot */}
                <div className="relative z-10 flex-shrink-0 mt-0.5">
                  {isStatus ? (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${isDark ? 'bg-gray-800' : 'bg-white'} ${
                      evt.to_status === 'occupied' ? 'border-green-500' :
                      evt.to_status === 'leased' ? 'border-blue-500' :
                      evt.to_status === 'notice' ? 'border-orange-500' :
                      evt.to_status === 'vacant' ? 'border-red-500' : 'border-gray-400'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${STATUS_BG[evt.to_status] || 'bg-gray-400'}`} />
                    </div>
                  ) : (
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-purple-500 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                      <Tag size={10} className="text-purple-500" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {/* Main description */}
                      {isStatus && (
                        <p className={`text-xs ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {showUnit && <span className="font-medium">Unit {evt.unit_number}</span>}
                          {showUnit && ' — '}
                          <span className={STATUS_COLORS[evt.from_status] || ''}>{evt.from_status || 'new'}</span>
                          <ArrowRight size={10} className="inline mx-1 text-gray-400" />
                          <span className={`font-medium ${STATUS_COLORS[evt.to_status] || ''}`}>{evt.to_status}</span>
                        </p>
                      )}
                      {isDesignation && (
                        <p className={`text-xs ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {showUnit && <span className="font-medium">Unit {evt.unit_number}</span>}
                          {showUnit && ' — '}
                          <span className="text-purple-500">{evt.description}</span>
                        </p>
                      )}

                      {/* Meta line */}
                      <div className={`flex items-center gap-2 mt-0.5 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {showBuilding && evt.building_name && (
                          <span>{evt.building_name}</span>
                        )}
                        {evt.designation && isStatus && (
                          <span className={`px-1 py-0 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>{evt.designation}%</span>
                        )}
                        {isDesignation && evt.reason && (
                          <span className="capitalize">{evt.reason.replace(/_/g, ' ')}</span>
                        )}
                        {evt.changed_by && (
                          <span>by {evt.changed_by}</span>
                        )}
                      </div>

                      {/* Notes */}
                      {isDesignation && evt.notes && (
                        <p className={`text-[10px] mt-0.5 italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          "{evt.notes}"
                        </p>
                      )}
                    </div>

                    {/* Timestamp */}
                    <div className={`text-right flex-shrink-0 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <div>{formatDate(evt.timestamp)}</div>
                      <div>{formatTime(evt.timestamp)}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {total} event{total !== 1 ? 's' : ''} total
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <ChevronLeft size={14} />
            </button>
            <span className={`text-xs px-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {loading && data && (
        <div className="flex justify-center py-2">
          <Loader2 className="animate-spin text-blue-500" size={14} />
        </div>
      )}
    </div>
  );
}
